import axios from "axios";
import { Dispatch } from "redux";
import { StoreActionTypes, StoreActions } from "../actionTypes/storeActions";
import { StoreDetailsProps } from "../components/storeDetails/storeDetails";
import { CardPaymentInterface, StripePaymentInterface } from "../interfaces/cardPaymentInterface";
import { StoreDetailsResponseInterface, StorePaymentResponseInterface } from "../interfaces/storeInterfaces";
import API_URL from "../config/api";
import { encrypt } from "../lib/crypto.lib";
export const getStoreDetails =
  (params: StoreDetailsProps) => async (dispatch: Dispatch<StoreActions>) => {
    dispatch({
      type: StoreActionTypes.GET_STORE_DETAILS,
      payload: {
        isLoading: true,
        success: null,
        error: null,
      },
    });

    try {
      const storeDetails = await axios({
        method: "GET",
        params,
        url: API_URL.STORE_API,
      });

      return dispatch({
        type: StoreActionTypes.GET_STORE_DETAILS,
        payload: {
          isLoading: false,
          success: storeDetails.data,
          error: null,
        },
      });
    } catch (error) {
      return dispatch({
        type: StoreActionTypes.GET_STORE_DETAILS,
        payload: {
          isLoading: false,
          success: null,
          error: (error as Error).message,
        },
      });
    }
  };

export const makeStorePaymentAction =
  (params: CardPaymentInterface) =>
  async (dispatch: Dispatch<StoreActions>) => {
    dispatch({
      type: StoreActionTypes.MAKE_PAYMENT,
      payload: {
        isLoading: true,
        success: null,
        error: null,
      }
    });

    // Make freshStoreDetails available in catch for failure hook
    let freshStoreDetails: StoreDetailsResponseInterface | null = null;

    try {
      // Fetch fresh store details before processing payment
      // Use authKey and token from params to fetch fresh details
      const storeParams = {
        authKey: params.authKey || '',
        token: params.token || '',
        storeId: params.storeId
      };

      const storeDetails = await axios.get<StoreDetailsResponseInterface>(API_URL.STORE_API, {
        params: storeParams,
      });

      // Use fresh store details for payment
      freshStoreDetails = storeDetails.data;

      // Dispatch fresh store details to update the store
      dispatch({
        type: StoreActionTypes.GET_STORE_DETAILS,
        payload: {
          isLoading: false,
          success: freshStoreDetails,
          error: null,
        },
      });

      if (freshStoreDetails && freshStoreDetails.storeDetails) {
        const { postcode, id } = freshStoreDetails.storeDetails;
        const transactionUnique = freshStoreDetails.transactionId?.toString() || '';
        const invoicesNew = freshStoreDetails.billingDetails?.invoiceIds || [];

        // Update params with fresh store details
        const updatedParams = {
          ...params,
          customerPostCode: postcode,
          transactionUnique,
          storeId: id,
          invoices: invoicesNew
        };

        // Encrypt sensitive data
        updatedParams.cardCVV = encrypt(params.cardCVV);
        updatedParams.cardNumber = encrypt(params.cardNumber);

        const paymentDetails = await axios({
          method: "POST",
          url: API_URL.API_BILLING,
          data: updatedParams,
          headers: {
            "requested-by": "PAY-BY-LINK",
          },
        }) as { data: StorePaymentResponseInterface };

        // Append success action to the URL without reloading the page
        if (typeof window !== 'undefined' && paymentDetails?.data?.paymentStatus === 1) {
          const url = new URL(window.location.href);
          url.searchParams.set('action', 'success');
          window.history.pushState({}, '', url);
        }

        // Call billing hook (redirectUrl) with SUCCESS/FAILURE
        try {
          const redirectUrl: string | undefined = (freshStoreDetails as any)?.billingDetails?.redirectUrl;
          if (redirectUrl) {
            const statusString = paymentDetails.data.paymentStatus === 1 ? 'SUCCESS' : 'FAILURE';
            await axios({
              method: 'POST',
              url: redirectUrl,
              headers: {
                Authorization: API_URL.APP_HOOK_AUTH_TOKEN,
                'Content-Type': 'application/json',
              },
              data: {
                reference: freshStoreDetails.transactionId,
                storeId: freshStoreDetails.storeDetails.id,
                status: statusString,
              },
            });
          }
        } catch (hookError) {
          // Do not block main flow on hook failure
          // eslint-disable-next-line no-console
          console.error('Billing hook call failed', hookError);
        }

        return dispatch({
          type: StoreActionTypes.MAKE_PAYMENT,
          payload: {
            isLoading: false,
            success: paymentDetails.data,
            error: null,
          },
        });
      } else {
        throw new Error('Failed to fetch fresh store details');
      }
    } catch (error) {
      // Attempt FAILURE hook if redirectUrl available
      try {
        const redirectUrl: string | undefined = (freshStoreDetails as any)?.billingDetails?.redirectUrl;
        if (redirectUrl && freshStoreDetails) {
          await axios({
            method: 'POST',
            url: redirectUrl,
            headers: {
              Authorization: API_URL.APP_HOOK_AUTH_TOKEN,
              'Content-Type': 'application/json',
            },
            data: {
              reference: freshStoreDetails.transactionId,
              storeId: freshStoreDetails.storeDetails.id,
              status: 'FAILURE',
            },
          });
        }
      } catch (hookError) {
        // eslint-disable-next-line no-console
        console.error('Billing hook FAILURE call failed', hookError);
      }

      return dispatch({
        type: StoreActionTypes.MAKE_PAYMENT,
        payload: {
          isLoading: false,
          success: null,
          error: (error as Error).message,
        },
      });
    }
  };

export const makeStripePaymentAction =
  (params: StripePaymentInterface) =>
  async (dispatch: Dispatch<StoreActions>) => {
    dispatch({
      type: StoreActionTypes.MAKE_PAYMENT,
      payload: {
        isLoading: true,
        success: null,
        error: null,
      }
    });

    let freshStoreDetails: StoreDetailsResponseInterface | null = null;

    try {
      const storeParams = {
        authKey: params.authKey || '',
        token: params.token || '',
        storeId: params.storeId
      };

      const storeDetails = await axios.get<StoreDetailsResponseInterface>(API_URL.STORE_API, {
        params: storeParams,
      });

      freshStoreDetails = storeDetails.data;

      dispatch({
        type: StoreActionTypes.GET_STORE_DETAILS,
        payload: {
          isLoading: false,
          success: freshStoreDetails,
          error: null,
        },
      });

      if (freshStoreDetails && freshStoreDetails.storeDetails) {
        const { id } = freshStoreDetails.storeDetails;
        const invoicesNew = freshStoreDetails.billingDetails?.invoiceIds || [];

        const jwtToken = freshStoreDetails.storeDetails.token;

  
        const cleanPayload = {
          action: "",
          actionType: "invoiceNewCardAdd",
          cardCVV: "",
          cardExpiryMonth: params.cardExpiryMonth,
          cardExpiryYear: params.cardExpiryYear,
          cardNumber: params.cardNumber.slice(-4),
          customerName: params.customerName,
          customerPostCode: "",
          invoices: invoicesNew,
          isBobcrm: true,
          orderRef: params.orderRef,
          paymentMethodId: params.paymentMethodId,
          provider: "STRIPE",
          storeId: id.toString(),
          type: "OSB",
          transactionUnique: freshStoreDetails.transactionId.split(":")[0],
          isFrom:"PAY-BY-LINK",

        };

        const saveCardUrl = `${API_URL.SAVE_CARD_URL}/v1/${id}/save-card`;
        console.log("saveCardUrl ==>", saveCardUrl);
        const paymentDetails = await axios({
          method: "POST",
          url: saveCardUrl,
          data: cleanPayload,
          headers: {
            "requested-by": "PAY-BY-LINK",
            "Content-Type": "application/json",
            "Authorization": `${jwtToken}`,
          },
        }) as { data: StorePaymentResponseInterface };

        if (typeof window !== 'undefined' && paymentDetails?.data?.paymentStatus === 1) {
          const url = new URL(window.location.href);
          url.searchParams.set('action', 'success');
          const last4Digits = paymentDetails?.data?.transactionLog?.last4Digits || params.cardNumber.slice(-4);
          const cardIssuer = paymentDetails?.data?.transactionLog?.cardIssuer || '';
          url.searchParams.set('cardIssuer', cardIssuer);
          url.searchParams.set('last4', last4Digits);
          url.searchParams.set('amount', freshStoreDetails.billingDetails?.totalAmount?.toString() || '0');
          url.searchParams.set('txId', paymentDetails.data.manageMypayTransactionLogId || freshStoreDetails.transactionId?.toString() || '');
          window.history.pushState({}, '', url);
        }

        // Call billing hook (redirectUrl) with SUCCESS/FAILURE
        try {
          const redirectUrl: string | undefined = (freshStoreDetails as any)?.billingDetails?.redirectUrl;
          if (redirectUrl) {
            const statusString = paymentDetails.data.paymentStatus === 1 ? 'SUCCESS' : 'FAILURE';
            await axios({
              method: 'POST',
              url: redirectUrl,
              headers: {
                Authorization: API_URL.APP_HOOK_AUTH_TOKEN,
                'Content-Type': 'application/json',
              },
              data: {
                reference: freshStoreDetails.transactionId,
                storeId: freshStoreDetails.storeDetails.id,
                status: statusString,
              },
            });
          }
        } catch (hookError) {
          // eslint-disable-next-line no-console
          console.error('Stripe billing hook call failed', hookError);
        }

        return dispatch({
          type: StoreActionTypes.MAKE_PAYMENT,
          payload: {
            isLoading: false,
            success: paymentDetails.data,
            error: null,
          },
        });
      } else {
        throw new Error('Failed to fetch fresh store details for Stripe payment');
      }
    } catch (error) {
      // Attempt FAILURE hook if redirectUrl available
      try {
        const redirectUrl: string | undefined = (freshStoreDetails as any)?.billingDetails?.redirectUrl;
        if (redirectUrl && freshStoreDetails) {
          await axios({
            method: 'POST',
            url: redirectUrl,
            headers: {
              Authorization: API_URL.APP_HOOK_AUTH_TOKEN,
              'Content-Type': 'application/json',
            },
            data: {
              reference: freshStoreDetails.transactionId,
              storeId: freshStoreDetails.storeDetails.id,
              status: 'FAILURE',
            },
          });
        }
      } catch (hookError) {
        // eslint-disable-next-line no-console
        console.error('Stripe billing hook FAILURE call failed', hookError);
      }

      return dispatch({
        type: StoreActionTypes.MAKE_PAYMENT,
        payload: {
          isLoading: false,
          success: null,
          error: (error as Error).message,
        },
      });
    }
  };
